import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';

const SignUp = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    country: 'البحرين'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation: Username (English only)
    if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      setError(t('pages.signUp.usernameRequired'));
      return;
    }

    // Validation: Password (8+ chars with letters and numbers)
    if (formData.password.length < 8) {
      setError(t('pages.signUp.passwordMin'));
      return;
    }

    const hasLetter = /[a-zA-Z]/.test(formData.password);
    const hasNumber = /[0-9]/.test(formData.password);
    if (!hasLetter || !hasNumber) {
      setError(t('pages.signUp.passwordChars'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('pages.signUp.passwordMismatch'));
      return;
    }

    // Validation: Phone number (8 digits after +973)
    if (!/^\d{8}$/.test(formData.phoneNumber)) {
      setError(t('pages.signUp.phoneInvalid'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await signUp(
        formData.email, 
        formData.password, 
        formData.username,
        formData.phoneNumber
      );
      
      if (error) {
        if (error.message.includes('already registered')) {
          setError(t('pages.signUp.alreadyRegistered'));
        } else {
          setError(error.message);
        }
      } else {
        // Show success animation
        setShowSuccess(true);
        
        // Navigate to home after animation
        setTimeout(() => {
          navigate('/');
        }, 2500);
      }
    } catch (err: any) {
      setError(t('pages.signUp.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex" dir={dir}>
      {/* Success Animation Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-green-400 mb-2">{t('pages.signUp.success')}</h2>
            <p className="text-gray-300">{t('pages.signUp.redirecting')}</p>
          </div>
        </div>
      )}

      {/* Left Side - Image (Desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/auth-pattern.png" 
            alt="Background" 
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        </div>
        <div className="relative z-10 flex flex-col justify-center p-16">
          <h2 className="text-5xl font-bold text-white mb-4">
            {t('pages.signUp.joinAudome')}
          </h2>
          <p className="text-xl text-gray-300">
            {t('pages.signUp.startJourney')}
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">{t('pages.signUp.title')}</h1>
            <p className="text-gray-400">{t('pages.signUp.subtitle')}</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-white">
                {t('pages.signUp.username')} <span className="text-red-400">*</span>
              </label>
              <input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                className="w-full bg-white/5 border-white/20 text-white px-4 py-3 rounded-lg focus:border-[#1e48e0] focus:ring-1 focus:ring-[#1e48e0] transition-all h-11"
                placeholder="Ahmed"
                required
                disabled={loading}
                dir="ltr"
              />
              <p className="text-xs text-gray-500">{t('pages.signUp.usernameHint')}</p>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-white">
                {t('pages.signUp.email')} <span className="text-red-400">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-white/5 border-white/20 text-white px-4 py-3 rounded-lg focus:border-[#1e48e0] focus:ring-1 focus:ring-[#1e48e0] transition-all h-11"
                placeholder="example@email.com"
                required
                disabled={loading}
                dir="ltr"
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-white">
                {t('pages.signUp.phone')} <span className="text-red-400">*</span>
              </label>
              <div className="flex gap-2 w-full">
                <div className="bg-white/5 border-white/20 text-gray-400 px-3 py-3 rounded-lg flex items-center gap-1 flex-shrink-0 h-11">
                  <span className="text-white text-sm">🇧🇭</span>
                  <span dir="ltr" className="text-sm">+973</span>
                </div>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/\D/g, '').substring(0, 8) })}
                  className="flex-1 min-w-0 bg-white/5 border-white/20 text-white px-4 py-3 rounded-lg focus:border-[#1e48e0] focus:ring-1 focus:ring-[#1e48e0] transition-all h-11"
                  placeholder="12345678"
                  required
                  disabled={loading}
                  dir="ltr"
                />
              </div>
              <p className="text-xs text-gray-500">{t('pages.signUp.phoneHint')}</p>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-white">
                {t('pages.signUp.password')} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-white/5 border-white/20 text-white px-4 py-3 pr-12 rounded-lg focus:border-[#1e48e0] focus:ring-1 focus:ring-[#1e48e0] transition-all h-11"
                  placeholder="8+ أحرف مع حروف وأرقام"
                  required
                  disabled={loading}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500">{t('pages.signUp.passwordHint')}</p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-white">
                {t('pages.signUp.confirmPassword')} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full bg-white/5 border-white/20 text-white px-4 py-3 pr-12 rounded-lg focus:border-[#1e48e0] focus:ring-1 focus:ring-[#1e48e0] transition-all h-11"
                  placeholder={t('pages.signUp.confirmPassword')}
                  required
                  disabled={loading}
                  dir="ltr"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1e48e0] hover:bg-[#163aa8] text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center h-12 text-lg mt-6"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 ml-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('pages.signUp.creating')}
                </>
              ) : (
                t('pages.signUp.createAccount')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-zinc-900 text-gray-500">{t('common.or')}</span>
            </div>
          </div>

          {/* Sign In Link */}
          <p className="text-center text-gray-400 text-sm">
            {t('pages.signUp.hasAccount')}{' '}
            <Link to="/signin" className="text-blue-500 hover:text-blue-400 font-semibold transition-colors">
              {t('pages.signUp.signIn')}
            </Link>
          </p>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-400 transition-colors">
            {t('pages.signUp.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
